/**
 * FIX-APPRLIVE (D-257) — the customer portal's live reaction to the approval
 * notices on the shared notifications poll ('ticket_approval_requested' for the
 * approver, 'ticket_approval_decided' for the person who opened the request).
 * Pure; unit-tested in utils/__tests__/portalApprovalLive.test.ts.
 */
import type { Notification } from '../types';

export interface ApprovalArrival {
  id: string;
  kind: 'requested' | 'decided';
  title: string;
  requesterName: string;
  decision: string;
  link: string | null;
}

/** Query keys (prefixes) that show approvals / requests / their counters in the portal. */
export const PORTAL_APPROVAL_QUERY_KEYS: ReadonlyArray<readonly string[]> = [
  ['portal', 'ticket-approvals'],
  ['portal', 'ticket-approvals-count'],
  ['portal', 'requests'],
  ['portal', 'request'],
  ['portal', 'dashboard'],
  ['portal', 'pending-actions'],
  ['portal', 'recent-notifications'],
];

const str = (v: unknown) => (typeof v === 'string' ? v : '');

export function toApprovalArrival(n: Notification): ApprovalArrival | null {
  if (n.event_type !== 'ticket_approval_requested' && n.event_type !== 'ticket_approval_decided') return null;
  const m = (n.metadata ?? {}) as Record<string, unknown>;
  return {
    id: n.id,
    kind: n.event_type === 'ticket_approval_requested' ? 'requested' : 'decided',
    title: str(m.title),
    requesterName: str(m.requester_name),
    decision: str(m.decision),
    link: n.link ?? null,
  };
}

/**
 * Approval notices not seen before. The first poll only records what already
 * exists (no toast storm on login); `known` is updated in place.
 */
export function newApprovalArrivals(rows: Notification[], known: Set<string>, initialized: boolean): ApprovalArrival[] {
  const out: ApprovalArrival[] = [];
  for (const n of rows) {
    if (known.has(n.id)) continue;
    known.add(n.id);
    if (!initialized) continue;
    const a = toApprovalArrival(n);
    if (a) out.push(a);
  }
  return out;
}
